import { groupSessionColumns, splitBoardSessions } from "../../domain/round/sessions";
import type { AssignmentSegment, NormalizedRoundSchedule, RoundAssignment, RoundGroup } from "../../domain/round/types";

const SLOT_COLUMNS: Array<{ segment: AssignmentSegment; label: string; segments: AssignmentSegment[] }> = [
  { segment: "full18", label: "18", segments: ["full18", "extraFull18"] },
  { segment: "holes1to9", label: "1–9", segments: ["holes1to9", "extra1to9"] },
  { segment: "holes10to18", label: "10–18", segments: ["holes10to18", "extra10to18"] },
];

export type ManualBoardTableCallbacks = {
  readOnly: boolean;
  selectedAssignmentId: string;
  selectedQuickPersonId: string;
  selectedQuickPersonName: string;
  peopleById: Map<string, string>;
  onAssignmentClick: (assignmentId: string) => void;
  onMoveSelected: (groupId: string, segment: AssignmentSegment) => void;
  onAssignQuickPerson: (groupId: string, segment: AssignmentSegment) => void;
  onOpenPersonPicker: (groupId: string, segment: AssignmentSegment) => void;
  onToggleLock: (assignment: RoundAssignment) => void;
  onRemove: (assignment: RoundAssignment) => void;
};

function clock(minutes: number | null | undefined) {
  if (minutes == null || !Number.isFinite(minutes)) return "—";
  const value = Math.max(0, Math.round(minutes));
  return `${String(Math.floor(value / 60)).padStart(2, "0")}:${String(value % 60).padStart(2, "0")}`;
}

function SlotCell({
  schedule,
  group,
  segment,
  acceptedSegments,
  callbacks,
}: {
  schedule: NormalizedRoundSchedule;
  group: RoundGroup;
  segment: AssignmentSegment;
  acceptedSegments: AssignmentSegment[];
  callbacks: ManualBoardTableCallbacks;
}) {
  const assignments = schedule.assignments.filter(
    (assignment) => assignment.groupId === group.id && acceptedSegments.includes(assignment.segment),
  );
  const selectedAssignment = schedule.assignments.find((assignment) => assignment.id === callbacks.selectedAssignmentId);
  const canMoveSelected = Boolean(
    selectedAssignment &&
      (selectedAssignment.groupId !== group.id || !acceptedSegments.includes(selectedAssignment.segment)),
  );
  const canQuickAssign = Boolean(callbacks.selectedQuickPersonId);

  return (
    <td className={`mbt-slot-cell ${canMoveSelected || canQuickAssign ? "target" : ""}`}>
      <div className="mbt-slot-content">
        {assignments.map((assignment) => (
          <div
            className={`mbt-person-chip ${callbacks.selectedAssignmentId === assignment.id ? "selected" : ""} ${assignment.locked ? "locked" : ""}`}
            key={assignment.id}
          >
            <button
              type="button"
              className="mbt-person-main"
              disabled={callbacks.readOnly}
              onClick={() => callbacks.onAssignmentClick(assignment.id)}
              title={assignment.locked ? "Pozycja zablokowana" : "Kliknij, aby zaznaczyć lub zamienić"}
            >
              {callbacks.peopleById.get(assignment.personId) || assignment.personId}
            </button>
            <button
              type="button"
              className="mbt-icon-action"
              disabled={callbacks.readOnly}
              onClick={() => callbacks.onToggleLock(assignment)}
              title={assignment.locked ? "Odblokuj" : "Zablokuj"}
            >
              {assignment.locked ? "●" : "○"}
            </button>
            <button
              type="button"
              className="mbt-icon-action danger"
              disabled={callbacks.readOnly || assignment.locked}
              onClick={() => callbacks.onRemove(assignment)}
              title="Usuń z tej pozycji"
            >
              ×
            </button>
          </div>
        ))}

        {!assignments.length ? <span className="mbt-empty-slot">—</span> : null}

        {!callbacks.readOnly ? (
          <div className="mbt-slot-actions">
            {canMoveSelected ? (
              <button type="button" className="mbt-inline-action move" onClick={() => callbacks.onMoveSelected(group.id, segment)}>
                Przenieś zaznaczonego
              </button>
            ) : null}
            {canQuickAssign ? (
              <button type="button" className="mbt-inline-action quick" onClick={() => callbacks.onAssignQuickPerson(group.id, segment)}>
                + {callbacks.selectedQuickPersonName || "scorer"}
              </button>
            ) : null}
            <button type="button" className="mbt-add-person" onClick={() => callbacks.onOpenPersonPicker(group.id, segment)} title="Dodaj dowolnego scorera">
              +
            </button>
          </div>
        ) : null}
      </div>
    </td>
  );
}

function TeeTable({
  schedule,
  title,
  groups,
  callbacks,
}: {
  schedule: NormalizedRoundSchedule;
  title: string;
  groups: RoundGroup[];
  callbacks: ManualBoardTableCallbacks;
}) {
  return (
    <section className="mbt-tee-section">
      <header className="mbt-tee-title">
        <strong>{title}</strong>
        <span>{groups.length} grup</span>
      </header>
      <div className="mbt-table-scroll">
        <table className="mbt-table">
          <thead>
            <tr>
              <th className="mbt-col-group">Grupa</th>
              <th className="mbt-col-time">Start</th>
              <th className="mbt-col-players">Gracze</th>
              {SLOT_COLUMNS.map((column) => <th className="mbt-col-slot" key={column.segment}>{column.label}</th>)}
            </tr>
          </thead>
          <tbody>
            {groups.map((group) => (
              <tr key={group.id} className={group.tv ? "tv-row" : ""}>
                <td className="mbt-group-cell">
                  <strong>G{group.number}</strong>
                  {group.tv ? <span>TV</span> : null}
                </td>
                <td className="mbt-time-cell">
                  <strong>{clock(group.startMinutes)}</strong>
                  <small>9 {clock(group.finish9Minutes)} · 18 {clock(group.finish18Minutes)}</small>
                </td>
                <td className="mbt-player-cell">{group.playerNames.length ? group.playerNames.join(" · ") : "—"}</td>
                {SLOT_COLUMNS.map((column) => (
                  <SlotCell
                    key={column.segment}
                    schedule={schedule}
                    group={group}
                    segment={column.segment}
                    acceptedSegments={column.segments}
                    callbacks={callbacks}
                  />
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

export function ManualBoardTable({ schedule, callbacks }: { schedule: NormalizedRoundSchedule; callbacks: ManualBoardTableCallbacks }) {
  const sessions = splitBoardSessions(schedule);

  return (
    <div className={`mbt-session-grid ${sessions.length === 1 ? "single" : "double"}`}>
      {sessions.map((session) => {
        const columns = groupSessionColumns(session);
        return (
          <article className={`mbt-session mbt-session-${session.id}`} key={session.id}>
            <header className="mbt-session-header">
              <div>
                <span>{session.id === "morning" ? "AM" : session.id === "afternoon" ? "PM" : "WEEKEND"}</span>
                <h3>{session.title}</h3>
              </div>
              <strong>{session.groups.length} grup</strong>
            </header>
            <div className="mbt-session-body">
              {columns.map((column) => (
                <TeeTable key={column.id} schedule={schedule} title={column.title} groups={column.groups} callbacks={callbacks} />
              ))}
              {!columns.length ? <div className="mbt-no-groups">Brak grup w tej sesji.</div> : null}
            </div>
          </article>
        );
      })}
    </div>
  );
}
